import { Injectable } from '@angular/core';

/**
 * Persiste los filtros aplicados en pantallas SYS02TIP=LST (advancedtable).
 *
 * Globales (M05COD, M18COD, M19COD, CNT05COD) → localStorage (sobreviven cierre de browser).
 * Particulares por pantalla (resto de campos)  → sessionStorage (solo durante la sesion del browser).
 *
 * Reglas:
 * - Cuando el usuario aplica un filtro en una LST se descompone: campos globales van al
 *   bucket global, el resto al bucket particular del SYS02CON.
 * - Cuando se cambia la sede/compañia (M05COD/CNT05COD) desde el header, se limpian todos
 *   los particulares y se reinician los globales con los nuevos valores. M18/M19 se vacian.
 */
@Injectable({ providedIn: 'root' })
export class FiltrosPersistenciaService {
  private readonly CAMPOS_GLOBALES = ['M05COD', 'M18COD', 'M19COD', 'CNT05COD'];
  private readonly KEY_GLOBAL = 'AVATAR_FILTROS_GLOBAL';
  private readonly PREFIX_PANT = 'AVATAR_FILTROS_PANT_';

  getGlobales(): { [k: string]: string } {
    try {
      const raw = localStorage.getItem(this.KEY_GLOBAL);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private setGlobales(obj: { [k: string]: string }): void {
    try {
      localStorage.setItem(this.KEY_GLOBAL, JSON.stringify(obj || {}));
    } catch { /* quota / private mode */ }
  }

  getParticulares(intSYS02CON: string | number): { [k: string]: any } {
    if (intSYS02CON == null || intSYS02CON === '') return {};
    try {
      const raw = sessionStorage.getItem(this.PREFIX_PANT + intSYS02CON);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private setParticulares(intSYS02CON: string | number, obj: { [k: string]: any }): void {
    if (intSYS02CON == null || intSYS02CON === '') return;
    try {
      if (!obj || Object.keys(obj).length === 0) {
        sessionStorage.removeItem(this.PREFIX_PANT + intSYS02CON);
      } else {
        sessionStorage.setItem(this.PREFIX_PANT + intSYS02CON, JSON.stringify(obj));
      }
    } catch { /* quota / private mode */ }
  }

  /**
   * Variante de getAplicables que NO filtra por campos disponibles.
   * Usar en el PRIMER fetch del advancedtable para evitar el doble-llamado:
   * el backend de ListarGenerico (AvatarDB.java) ignora los nombres de
   * filtro que no estan en el catalogo SYS03ARC de la pantalla, asi que es
   * seguro mandar el merge completo desde la primera llamada.
   *
   * Si en algun escenario el backend dejara de ser tolerante con nombres
   * desconocidos, volver a usar getAplicables tras conocer FiltrosAvanzado.
   */
  getFiltrosIniciales(intSYS02CON: string | number): { [k: string]: any } {
    const globales = this.getGlobales();
    const particulares = this.getParticulares(intSYS02CON);
    const merge = { ...globales, ...particulares };
    const out: { [k: string]: any } = {};
    Object.keys(merge).forEach(k => {
      const v = merge[k];
      if (v !== '' && v != null) out[k] = v;
    });
    return out;
  }

  /**
   * Devuelve el merge globales + particulares filtrado por los campos disponibles en la pantalla.
   * Particulares pisan globales si hay colision (mas especificos).
   * Si camposDisponibles esta vacio devuelve {} (no se puede aplicar nada).
   */
  getAplicables(intSYS02CON: string | number, camposDisponibles?: string[]): { [k: string]: any } {
    if (!camposDisponibles || camposDisponibles.length === 0) return {};
    const globales = this.getGlobales();
    const particulares = this.getParticulares(intSYS02CON);
    const merge = { ...globales, ...particulares };
    const setCampos = new Set(camposDisponibles);
    const out: { [k: string]: any } = {};
    Object.keys(merge).forEach(k => {
      const v = merge[k];
      if (setCampos.has(k) && v !== '' && v != null) {
        out[k] = v;
      }
    });
    return out;
  }

  /**
   * Persiste un formulario aplicado: descompone y guarda M05/M18/M19/CNT05 en globales
   * y el resto en particulares del SYS02CON. Sobrescribe particulares previos.
   * Globales se hacen merge para no perder los que el usuario nunca toco en esta pantalla.
   */
  guardarFiltrosFromForm(intSYS02CON: string | number, formObj: any): void {
    if (formObj == null || typeof formObj !== 'object') return;
    const globales = this.getGlobales();
    const particulares: { [k: string]: any } = {};
    Object.keys(formObj).forEach(k => {
      const val = formObj[k];
      if (val === '' || val == null) return;
      if (Array.isArray(val)) {
        // Campos select_multiple: se conserva el arreglo (los vacios no se persisten).
        if (val.length > 0) particulares[k] = val;
        return;
      }
      const strVal = String(val);
      if (this.CAMPOS_GLOBALES.indexOf(k) >= 0) {
        globales[k] = strVal;
      } else {
        particulares[k] = strVal;
      }
    });
    this.setGlobales(globales);
    this.setParticulares(intSYS02CON, particulares);
  }

  limpiarParticulares(intSYS02CON: string | number): void {
    if (intSYS02CON == null || intSYS02CON === '') return;
    try {
      sessionStorage.removeItem(this.PREFIX_PANT + intSYS02CON);
    } catch { /* nop */ }
  }

  /**
   * Borra TODO el estado de filtros persistentes (globales en localStorage +
   * particulares de TODAS las pantallas en sessionStorage). A llamar desde el
   * logout para evitar que filtros del usuario anterior contaminen al siguiente
   * en computadores compartidos.
   */
  limpiarTodo(): void {
    try { localStorage.removeItem(this.KEY_GLOBAL); } catch { /* nop */ }
    this.limpiarTodosParticulares();
  }

  private limpiarTodosParticulares(): void {
    try {
      const remover: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.indexOf(this.PREFIX_PANT) === 0) remover.push(key);
      }
      remover.forEach(k => sessionStorage.removeItem(k));
    } catch { /* nop */ }
  }

  /**
   * A llamar ANTES de actualizar X-AVATAR-CNT05COD/M05COD/M18COD/M19COD en localStorage cuando
   * el usuario cambia los globales desde el header. Si CUALQUIERA de los 4 valores difiere del
   * actual (X-AVATAR-*), limpia todos los particulares y resetea los globales con los nuevos
   * 4 valores.
   *
   * Pasar undefined / null para los campos que NO se quieren actualizar en este llamado
   * (ej. el `obtenerLogo` del topbar solo conoce CNT05/M05, no debe tocar M18/M19): en ese
   * caso se mantienen los valores previos del localStorage para la comparacion.
   */
  cambiarContextoHeader(nuevoCNT05COD?: string, nuevoM05COD?: string, nuevoM18COD?: string, nuevoM19COD?: string): void {
    const prevCNT = localStorage.getItem('X-AVATAR-CNT05COD') || '';
    const prevM05 = localStorage.getItem('X-AVATAR-M05COD')   || '';
    const prevM18 = localStorage.getItem('X-AVATAR-M18COD')   || '';
    const prevM19 = localStorage.getItem('X-AVATAR-M19COD')   || '';
    // Si el caller pasa undefined => mantiene el valor previo (no toca ese campo).
    const newCNT = (nuevoCNT05COD === undefined ? prevCNT : (nuevoCNT05COD == null ? '' : String(nuevoCNT05COD)));
    const newM05 = (nuevoM05COD   === undefined ? prevM05 : (nuevoM05COD   == null ? '' : String(nuevoM05COD)));
    const newM18 = (nuevoM18COD   === undefined ? prevM18 : (nuevoM18COD   == null ? '' : String(nuevoM18COD)));
    const newM19 = (nuevoM19COD   === undefined ? prevM19 : (nuevoM19COD   == null ? '' : String(nuevoM19COD)));
    const hayCambio    = !(prevCNT === newCNT && prevM05 === newM05 && prevM18 === newM18 && prevM19 === newM19);
    // almora 2026.05.27 — Si los X-AVATAR-* no cambiaron pero el bucket de globales
    // quedo vacio (caso tipico: post-logout que llamo limpiarTodo) hay que repoblar
    // de todas formas. Sin esta guarda algunas pantallas no aplican CNT05/M05/M18/M19
    // por defecto tras el primer login despues de un logout.
    const globalesVacios = Object.keys(this.getGlobales()).length === 0;
    if (!hayCambio && !globalesVacios) return;
    if (hayCambio) this.limpiarTodosParticulares();
    const g: { [k: string]: string } = {};
    if (newCNT) g['CNT05COD'] = newCNT;
    if (newM05) g['M05COD']   = newM05;
    if (newM18) g['M18COD']   = newM18;
    if (newM19) g['M19COD']   = newM19;
    this.setGlobales(g);
    // Sincroniza los X-AVATAR-* solo de los campos que el caller modifico explicitamente
    // (los otros los actualiza el caller fuera de este metodo).
    if (nuevoM18COD !== undefined) {
      try { localStorage.setItem('X-AVATAR-M18COD', newM18); } catch { /* nop */ }
    }
    if (nuevoM19COD !== undefined) {
      try { localStorage.setItem('X-AVATAR-M19COD', newM19); } catch { /* nop */ }
    }
  }
}
